import {
  ScaffolderFormPaper,
  ScaffolderFormHeader,
  ScaffolderInputHeader,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useCallback, useEffect, useRef } from 'react';

import { Box, Checkbox, FormControlLabel } from '@material-ui/core';

import { MetadataKeyValueInput } from '../components/MetadataKeyValueInput';
import { AUTO_CREATE_NAMESPACE_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import {
  KubernetesMetadata,
  MetadataKeyValuePair,
  AutoCreateNamespaceConfig,
  MetadataField,
  DEFAULT_METADATA,
} from '../types';
import { metadataInputErrors } from '../validation';

interface AutoNamespaceSectionProps {
  value: AutoCreateNamespaceConfig;
  onChange: (value: AutoCreateNamespaceConfig) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}

const AutoNamespaceSectionInner = ({
  value,
  onChange,
  errors,
  enableAppSetSupport = false,
}: AutoNamespaceSectionProps) => {
  const classes = useStyles();
  const savedMetadataRef = useRef<KubernetesMetadata>(DEFAULT_METADATA);
  const sectionRef = useRef<HTMLDivElement>(null);
  const disabled = !value.enabled;
  const metadataError =
    errors.length > 0
      ? metadataInputErrors(
          value.managedNamespaceMetadata ?? DEFAULT_METADATA,
          {
            enableAppSetSupport,
          },
        )
      : '';
  const metadata = value.managedNamespaceMetadata;

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    if (metadataError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [metadataError]);

  const handleAutoCreateNamespaceEnabledChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChangeRef.current({
          enabled: checked,
          managedNamespaceMetadata: {
            annotations: savedMetadataRef.current.annotations,
            labels: savedMetadataRef.current.labels,
          },
        });
      } else {
        const m = metadataRef.current;
        savedMetadataRef.current = {
          annotations: m.annotations,
          labels: m.labels,
        };
        onChangeRef.current({
          enabled: checked,
          managedNamespaceMetadata: { annotations: [], labels: [] },
        });
      }
    },
    [],
  );

  const handleAddField = useCallback((field: MetadataField) => {
    const v = valueRef.current;
    const m = metadataRef.current;
    onChangeRef.current({
      ...v,
      managedNamespaceMetadata: {
        ...m,
        [field]: [...(m[field] || []), { key: '', value: '' }],
      },
    });
  }, []);

  const handleRemoveField = useCallback(
    (field: MetadataField, index: number) => {
      const v = valueRef.current;
      const m = metadataRef.current;
      onChangeRef.current({
        ...v,
        managedNamespaceMetadata: {
          ...m,
          [field]: (m[field] || []).filter((_, i) => i !== index),
        },
      });
    },
    [],
  );

  const handleChangeField = useCallback(
    (
      field: MetadataField,
      index: number,
      property: keyof MetadataKeyValuePair,
      fieldValue: string,
    ) => {
      const v = valueRef.current;
      const m = metadataRef.current;
      onChangeRef.current({
        ...v,
        managedNamespaceMetadata: {
          ...m,
          [field]: (m[field] || []).map((item, i) =>
            i === index ? { ...item, [property]: fieldValue } : item,
          ),
        },
      });
    },
    [],
  );
  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Automatic Namespace Creation"
          description="Configure automatic creation of the application's destination namespace."
          divider
          infoDoc={AUTO_CREATE_NAMESPACE_INFO}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Enable automatic namespace creation"
            description="Enable or disable automatic creation of the application's destination namespace."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={value.enabled}
                onChange={e =>
                  handleAutoCreateNamespaceEnabledChange(e.target.checked)
                }
                color="secondary"
              />
            }
            label="Enable automatic namespace creation"
          />
        </Box>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Annotations"
            description="Manage annotations for the application's destination namespace."
          />
        </Box>
        <MetadataKeyValueInput
          inputType="annotation"
          items={metadata.annotations || []}
          disabled={disabled}
          onAdd={() => handleAddField('annotations')}
          onRemove={index => handleRemoveField('annotations', index)}
          onChange={(index, property, val) =>
            handleChangeField('annotations', index, property, val)
          }
          errors={metadataError ? [metadataError] : []}
          enableAppSetSupport={enableAppSetSupport}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Labels"
            description="Manage labels for the application's destination namespace."
          />
        </Box>
        <MetadataKeyValueInput
          inputType="label"
          items={metadata.labels || []}
          disabled={disabled}
          onAdd={() => handleAddField('labels')}
          onRemove={index => handleRemoveField('labels', index)}
          onChange={(index, property, val) =>
            handleChangeField('labels', index, property, val)
          }
          errors={metadataError ? [metadataError] : []}
          enableAppSetSupport={enableAppSetSupport}
        />
        <Box className={classes.inputContainer}>
          {metadataError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={metadataError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const AutoNamespaceSection = React.memo(AutoNamespaceSectionInner);
